import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Grid/table view toggle + multi-select for admin listing pages.
 * @param {Array} items - Current loaded items
 * @param {(item: object) => string} getItemId
 * @param {{ defaultView?: 'grid'|'table', resetKey?: string }} options
 */
export function useAdminListView(items, getItemId, options = {}) {
  const { defaultView = 'grid', resetKey } = options;
  const [viewMode, setViewMode] = useState(defaultView);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [resetKey]);

  const toggleItem = useCallback((id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const isSelected = useCallback(
    (id) => selectedIds.has(String(id)),
    [selectedIds]
  );

  const selectAll = useCallback(() => {
    setSelectedIds(new Set((items || []).map((item) => String(getItemId(item)))));
  }, [items, getItemId]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const allSelected = useMemo(() => {
    if (!items?.length) return false;
    return items.every((item) => selectedIds.has(String(getItemId(item))));
  }, [items, getItemId, selectedIds]);

  const someSelected = useMemo(
    () => (items || []).some((item) => selectedIds.has(String(getItemId(item)))),
    [items, getItemId, selectedIds]
  );

  const selectedCount = selectedIds.size;

  return {
    viewMode,
    setViewMode,
    selectedIds,
    toggleItem,
    isSelected,
    selectAll,
    clearSelection,
    selectedCount,
    allSelected,
    someSelected
  };
}
